/** @file scopeTableOf: the sector table an entry gets under a creation scope's direction count (CR-002). */
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';

/**
 * Built-in table families by direction count (CR-002): every family ends in FACE_1; QUAD and FRONT_ONLY join the
 * MIRROR family at 4. FRONT_BACK_2 (left out of CR-002) only steps down to FACE_1.
 */
const FAMILIES: readonly Readonly<Partial<Record<number, string>>>[] = [
  { 16: 'MIRROR_16', 8: 'MIRROR_8', 4: 'MIRROR_4', 2: 'MIRROR_2', 1: 'FACE_1' },
  { 16: 'FACE_16', 8: 'FACE_8', 4: 'FACE_4', 2: 'FACE_2', 1: 'FACE_1' },
  { 16: 'QUAD_16', 8: 'QUAD_8', 4: 'MIRROR_4', 2: 'MIRROR_2', 1: 'FACE_1' },
  { 16: 'FRONT_ONLY_16', 8: 'FRONT_ONLY_8', 4: 'MIRROR_4', 2: 'MIRROR_2', 1: 'FACE_1' },
  { 2: 'FRONT_BACK_2', 1: 'FACE_1' },
];

const FLIP = '_FLIP';

/** The family member of a non-twin id, or undefined when no family holds it. */
function memberOf(id: string, directions: number): string | undefined {
  for (const family of FAMILIES) {
    const own = Object.entries(family).find(([, member]) => member === id);
    if (own !== undefined) return Number(own[0]) <= directions ? id : family[directions];
  }
  return undefined;
}

/**
 * The table id `id` becomes for a puppet of `directions` body directions: the member of its family with that many
 * directions, never more than it has (a scope narrows: FACE_8 stays FACE_8 at 16). A `<id>_FLIP` twin maps to its
 * member's twin. `undefined` = no family member (a user table, or a twin the member lacks): leave the entry as is.
 */
export function scopeTableOf(id: string, directions: 1 | 2 | 4 | 8 | 16): string | undefined {
  if (!id.endsWith(FLIP)) return memberOf(id, directions);
  const base = id.slice(0, -FLIP.length);
  const member = memberOf(base, directions);
  if (member === undefined) return undefined;
  if (member === base) return id;
  return Object.hasOwn(SECTOR_TABLES, `${member}${FLIP}`) ? `${member}${FLIP}` : undefined;
}
